import React, { useState, useRef, useCallback, useMemo, useEffect } from 'react';
import { Segment, Tab, Icon } from 'semantic-ui-react';
import 'semantic-ui-css/semantic.min.css';
import SidebarMenu from './menu.js';
import Footer from './footer.js';
import ProfileSection from './components/ProfileSection';
import ExperienceSection from './components/ExperienceSection';
import EducationSection from './components/EducationSection';
import ProjectsSection from './components/ProjectsSection';
import AmbientScene from './components/AmbientScene';
import * as EN from './data/en';
import * as CN from './data/cn';
import * as FAKE_EN from './data/fake_en';
import * as FAKE_CN from './data/fake_cn';

const SWIPE_THRESHOLD = 80;

function getSystemDarkMode() {
  return window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches;
}

function makeMenuLabel(full, short, idx) {
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
      <span className="tab-label-full">{full}</span>
      <span className="tab-label-short">{short}</span>
      <span className="tab-number" aria-hidden="true">0{idx + 1}</span>
    </span>
  );
}

function App({ isPublic }) {
  const [lang, toggleLang] = useState(false);
  const [darkMode, setDarkMode] = useState(getSystemDarkMode());
  const [menuOpen, setMenuOpen] = useState(false);
  const [motionPaused, setMotionPaused] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const appRef = useRef(null);
  const dragStartX = useRef(null);

  // Mirror the theme onto <html> so page background / overscroll blend with the app
  useEffect(() => {
    document.documentElement.classList.toggle('dark-mode', darkMode);
  }, [darkMode]);

  useEffect(() => {
    document.documentElement.lang = lang ? 'zh' : 'en';
  }, [lang]);

  useEffect(() => {
    if (!window.IntersectionObserver) return;
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-visible');
          observer.unobserve(entry.target);
        }
      });
    }, { threshold: 0.06 });
    appRef.current.querySelectorAll('.agent-window').forEach((card, index) => {
      card.style.setProperty('--reveal-delay', `${Math.min(index, 3) * 70}ms`);
      card.classList.add('reveal-ready');
      observer.observe(card);
    });
    return () => observer.disconnect();
  }, [activeTab, lang, motionPaused]);

  // Data selection: public → fake, else real (with language toggle)
  const data = useMemo(() => {
    const realData = lang ? CN : EN;
    if (isPublic) {
      const fakeData = lang ? FAKE_CN : FAKE_EN;
      return { ...fakeData, projects: realData.projects };
    }
    return realData;
  }, [isPublic, lang]);

  const handleTouchStart = useCallback((e) => {
    dragStartX.current = e.touches[0].clientX;
  }, []);

  const handleTouchEnd = useCallback((e) => {
    if (dragStartX.current === null) return;
    const dx = e.changedTouches[0].clientX - dragStartX.current;
    if (dx > SWIPE_THRESHOLD) {
      setMenuOpen(true);
    } else if (dx < -SWIPE_THRESHOLD) {
      setMenuOpen(false);
    }
    dragStartX.current = null;
  }, []);

  const panes = useMemo(() => [
    {
      menuItem: { key: 'experience', icon: 'suitcase', content: makeMenuLabel(lang ? '经历' : 'Experience', 'EXP', 0) },
      render: () => <Tab.Pane><ExperienceSection experiences={data.experiences} lang={lang} isPublic={isPublic} motionPaused={motionPaused} /></Tab.Pane>,
    },
    {
      menuItem: { key: 'education', icon: 'graduation cap', content: makeMenuLabel(lang ? '教育' : 'Education', 'EDU', 1) },
      render: () => <Tab.Pane><EducationSection educationData={data.education} lang={lang} isPublic={isPublic} /></Tab.Pane>,
    },
    {
      menuItem: { key: 'projects', icon: 'terminal', content: makeMenuLabel(lang ? '项目' : 'Projects', 'PROJ', 2) },
      render: () => <Tab.Pane><ProjectsSection projects={data.projects} /></Tab.Pane>,
    },
  ], [lang, data, isPublic, motionPaused]);

  return (
    <div ref={appRef} className={`App${darkMode ? ' dark-mode' : ''}${motionPaused ? ' motion-paused' : ''}`} id="outer-container"
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}>
      <AmbientScene paused={motionPaused} />
      <SidebarMenu lang={lang} toggleLang={toggleLang} darkMode={darkMode} toggleDarkMode={() => setDarkMode(!darkMode)} menuOpen={menuOpen} setMenuOpen={setMenuOpen} isPublic={isPublic} />
      <header className="site-toolbar">
        <a className="site-wordmark" href="#page-wrap"><span className="wordmark-symbol">e.</span><span>ericpzh<span className="wordmark-slash"> / </span>{lang ? '个人主页' : 'portfolio'}</span></a>
        <div className="toolbar-actions">
          <button type="button" onClick={() => toggleLang(!lang)} aria-label={lang ? 'Switch to English' : '切换到中文'}>{lang ? 'EN' : '中文'}</button>
          <button type="button" onClick={() => setMotionPaused(!motionPaused)} aria-pressed={motionPaused} aria-label={motionPaused ? (lang ? '开启动画' : 'Enable animations') : (lang ? '暂停动画' : 'Pause animations')} title={motionPaused ? 'Enable animations' : 'Pause animations'}><Icon name={motionPaused ? 'play' : 'pause'} /></button>
          <button type="button" onClick={() => setDarkMode(!darkMode)} aria-label={darkMode ? (lang ? '浅色模式' : 'Use light theme') : (lang ? '深色模式' : 'Use dark theme')}><Icon name={darkMode ? 'sun' : 'moon'} /></button>
        </div>
      </header>
      <main id="page-wrap">
        <Segment.Group>
          <ProfileSection profileData={data.profile} isPublic={isPublic} lang={lang} />
          <div className="section-heading"><span>{lang ? '探索我的经历' : 'Explore my journey'}</span><span className="section-heading__detail">{lang ? '经历 / 学习 / 创作' : 'WORK / LEARN / BUILD'}</span></div>
          <Tab panes={panes} activeIndex={activeTab} onTabChange={(event, tab) => setActiveTab(tab.activeIndex)} />
        </Segment.Group>
      </main>
      <Footer />
    </div>
  );
}

export default App;
